import type { ThreadMessageLike } from "@assistant-ui/react";
import type { SocketInitialSessionIdentity } from "./socket/sessionIdentity";

export type SafetyExitMetadata = {
  kind: "triggered";
  source?: "sentinel" | "local" | "assistant_response";
  category?: string;
};

const SAFETY_EXIT_SESSION_OVERRIDE_STORAGE_KEY = "phoebe_safety_exit_session_override";
const CHAT_SESSION_IDENTITY_STORAGE_KEY = "phoebe_chat_session_identity";
const SAFETY_EXIT_CATEGORIES = new Set([
  // Official consolidated category (as of 9 Jul 2026)
  "Title IX DV - Survivor/ Assault/Victim/Rape/Abusive/Domestic Violence/Assault/Protective Order/Restraining Order/Altercation",
  // Legacy/backward-compat sub-categories
  "Title IX DV - Survivor",
  "Title IX DV - Rape/ Trafficking/ Domestic Violence",
  "Title IX DV - Rape",
  "Title IX DV - Trafficking",
  "Title IX DV - Victim",
  "Title IX DV - Abusive",
  "Title IX DV - Assault",
  "Title IX DV - Custody Conflict",
  "Title IX DV - Protective Order",
  // Backward-compat compound keys returned by older sentinel agent versions
  "Title IX DV - Survivor/ Assault",
  "Title IX DV - Victim/ Abusive/ Custody Conflict",
]);
const LOCAL_SAFETY_EXIT_TRIGGER_PATTERNS = [
  /\b(?:i am|i m|im)(?: a)? survivor\b/,
  /\b(?:i was|i have been|i ve been|ive been|was) (?:raped|sexually assaulted)\b/,
  /\b(?:i am|i m|im|i was|i have been|i ve been|ive been|being|been) (?:trafficked|trafficking)\b/,
  /\b(?:my|the) (?:partner|spouse|husband|wife|boyfriend|girlfriend|ex)\b.{0,120}\b(?:abusive|abuse|threaten|threatened|threatening|harass|harassing|unsafe|afraid|scared)\b/,
  /\b(?:abusive|abuse|threaten|threatened|threatening|harass|harassing|unsafe|afraid|scared)\b.{0,120}\b(?:partner|spouse|husband|wife|boyfriend|girlfriend|ex)\b/,
  /\b(?:i have|i got|i need|i filed|i m filing|im filing|my)\b.{0,120}\b(?:restraining order|protective order|no contact order|order of protection)\b/,
];
const LOCAL_NON_TITLE_IX_SURVIVOR_CONTEXT_PATTERN = /\bsurvivor of cancer\b/;
const SAFETY_EXIT_ASSISTANT_RESPONSE_PATTERNS = [
  /\b(?:if you feel you are in danger|need a safe place)\b.{0,240}\b(?:911|211)\b/,
  /\b(?:911|211)\b.{0,240}\b(?:need a safe place|if you feel you are in danger)\b/,
  /\btitleix vawa phoenix edu\b/,
];

type SafetyExitSessionOverride = {
  guestId: string;
  createdAt: string;
  resetGuest?: boolean;
};

export type SafetyExitSessionIdentityOverrideResult = {
  initialIdentity: SocketInitialSessionIdentity | undefined;
  resetGuestOnNextBootstrap: boolean;
};

type WithSafetyExitMetadataOptions = {
  allowLocalFallback?: boolean;
};

export function isSafetyExitTriggerMessage(value: string | undefined): boolean {
  const text = normalizeSafetyExitText(value);
  if (!text) return false;

  if (LOCAL_NON_TITLE_IX_SURVIVOR_CONTEXT_PATTERN.test(text)) return false;

  // Sentinel metadata is authoritative. These patterns only support local demos,
  // tests, and stale backend responses that predate the metadata contract.
  return LOCAL_SAFETY_EXIT_TRIGGER_PATTERNS.some((pattern) => pattern.test(text));
}

export function safetyExitMetadataFromAssistantText(value: string | undefined): SafetyExitMetadata | undefined {
  const text = normalizeSafetyExitText(value);
  if (!text) return undefined;
  if (!SAFETY_EXIT_ASSISTANT_RESPONSE_PATTERNS.some((pattern) => pattern.test(text))) {
    return undefined;
  }
  const hasVawaEmail = text.includes("titleix vawa phoenix edu");
  const hasSafetyResourceCopy = /\b(?:if you feel you are in danger|need a safe place)\b/.test(text)
    && /\b(?:911|211)\b/.test(text)
    && text.includes("title ix office");
  if (!hasVawaEmail && !hasSafetyResourceCopy) return undefined;
  return {
    kind: "triggered",
    source: "assistant_response",
  };
}

export function normalizeSafetyExitMetadata(value: unknown): SafetyExitMetadata | undefined {
  if (!isRecord(value)) return undefined;
  // Keep accepting the original object wire format while rejecting bare booleans
  // and other shapes that are too easy to set accidentally on unrelated payloads.
  if (value.kind !== "triggered" && value.triggered !== true) return undefined;

  const metadata: SafetyExitMetadata = { kind: "triggered" };
  if (value.source === "sentinel" || value.source === "local" || value.source === "assistant_response") {
    metadata.source = value.source;
  }
  const category = typeof value.category === "string" ? value.category.trim() : "";
  if (category) {
    metadata.category = category;
  }
  return metadata;
}

export function withSafetyExitMetadata(
  metadata: ThreadMessageLike["metadata"] | undefined,
  triggeringUserText: string | undefined,
  options: WithSafetyExitMetadataOptions = {},
): ThreadMessageLike["metadata"] {
  const custom = metadata?.custom ?? {};
  const existingSafetyExit = normalizeSafetyExitMetadata(custom.safetyExit);
  if (existingSafetyExit) {
    return {
      ...metadata,
      custom: {
        ...custom,
        safetyExit: existingSafetyExit,
      },
    };
  }
  const sanitizedCustom = Object.prototype.hasOwnProperty.call(custom, "safetyExit")
    ? omitSafetyExit(custom)
    : custom;
  const sanitizedMetadata = sanitizedCustom === custom ? metadata : { ...metadata, custom: sanitizedCustom };

  if (options.allowLocalFallback === false) return sanitizedMetadata ?? { custom: {} };
  if (!isSafetyExitTriggerMessage(triggeringUserText)) return sanitizedMetadata ?? { custom: {} };

  return {
    ...sanitizedMetadata,
    custom: {
      ...sanitizedCustom,
      safetyExit: {
        kind: "triggered",
        source: "local",
      } satisfies SafetyExitMetadata,
    },
  };
}

export function recordSafetyExitSessionReset(): SocketInitialSessionIdentity {
  const override = {
    guestId: createSafetyExitGuestId(),
    createdAt: new Date().toISOString(),
    resetGuest: true,
  } satisfies SafetyExitSessionOverride;
  writeSafetyExitSessionOverride(override);
  clearPersistedChatSessionIdentity();
  return { guestId: override.guestId };
}

export function createFreshChatSessionIdentity(): SocketInitialSessionIdentity {
  clearPersistedChatSessionIdentity();
  return { guestId: createSafetyExitGuestId() };
}

export function applySafetyExitSessionIdentityOverride(
  initialIdentity: SocketInitialSessionIdentity | undefined,
): SocketInitialSessionIdentity | undefined {
  return resolveSafetyExitSessionIdentityOverride(initialIdentity).initialIdentity;
}

export function resolveSafetyExitSessionIdentityOverride(
  initialIdentity: SocketInitialSessionIdentity | undefined,
): SafetyExitSessionIdentityOverrideResult {
  const override = readSafetyExitSessionOverride();
  if (!override) {
    return {
      initialIdentity,
      resetGuestOnNextBootstrap: false,
    };
  }

  return {
    initialIdentity: {
      ...initialIdentity,
      guestId: override.guestId,
      threadId: undefined,
      memoryId: undefined,
    },
    resetGuestOnNextBootstrap: override.resetGuest === true,
  };
}

export function clearSafetyExitSessionOverride(): void {
  try {
    getLocalStorage()?.removeItem(SAFETY_EXIT_SESSION_OVERRIDE_STORAGE_KEY);
  } catch {
    // Best-effort: leaving the marker only affects future guest reset attempts.
  }
}

function normalizeSafetyExitText(value: string | undefined): string {
  return (value ?? "")
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[']/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function omitSafetyExit(custom: Record<string, unknown>): Record<string, unknown> {
  const { safetyExit: _safetyExit, ...rest } = custom;
  return rest;
}

function createSafetyExitGuestId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && "randomUUID" in cryptoApi) {
    return `guest-${cryptoApi.randomUUID()}`;
  }
  return `guest-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readSafetyExitSessionOverride(): SafetyExitSessionOverride | null {
  try {
    const storage = getLocalStorage();
    const raw = storage?.getItem(SAFETY_EXIT_SESSION_OVERRIDE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SafetyExitSessionOverride>;
    if (!parsed.guestId || typeof parsed.guestId !== "string") return null;
    return {
      guestId: parsed.guestId,
      createdAt: typeof parsed.createdAt === "string" ? parsed.createdAt : "",
      resetGuest: parsed.resetGuest === true,
    };
  } catch {
    return null;
  }
}

function writeSafetyExitSessionOverride(override: SafetyExitSessionOverride): void {
  try {
    getLocalStorage()?.setItem(SAFETY_EXIT_SESSION_OVERRIDE_STORAGE_KEY, JSON.stringify(override));
  } catch {
    // Best-effort: if storage is blocked, the in-memory socket reset still clears the visible transcript.
  }
}

function clearPersistedChatSessionIdentity(): void {
  try {
    getLocalStorage()?.removeItem(CHAT_SESSION_IDENTITY_STORAGE_KEY);
  } catch {
    // Best-effort: if storage is blocked, the in-memory socket reset still clears the visible transcript.
  }
}

function getLocalStorage(): Storage | undefined {
  return typeof window === "undefined" ? undefined : window.localStorage;
}
